import React from "react";
import SeoPage from "../../components/SeoPage";
import RELATED from "../../data/related";
import { faqsByTopic } from "../../data/faqs";

export default function WhatIsCsvFile() {
    return (
        <SeoPage
            title="What Is a CSV File? — Plain English Guide"
            description="CSV stands for Comma-Separated Values. A CSV file stores tabular data as plain text. Here's what they are, why they matter, and when to use them."
            breadcrumbs={[{ label: "Home", href: "/" }, { label: "What Is a CSV File?" }]}
            eyebrow="/ basics"
            h1="What is a CSV file?"
            heroLead="A CSV file is a plain text file that stores tabular data — rows and columns — using commas to separate values. CSV stands for Comma-Separated Values. It's one of the simplest, oldest, and most widely supported data formats in the world."
            sections={[
                { heading: "An example, in 4 lines",
                    body: "If you opened a CSV file in a text editor, this is literally what you'd see:",
                    callout: <pre className="font-mono text-xs whitespace-pre-wrap">{`Name,Email,Country
John,john@email.com,Canada
Sarah,sarah@email.com,USA
Liam,liam@email.com,Ireland`}</pre>,
                },
                { heading: "How CSV works (in plain English)",
                    bullets: [
                        "Each line in the file = one row in the table",
                        "Each comma in a line = one column boundary",
                        "The first row is usually a 'header' naming each column",
                        "Values with commas inside are wrapped in double-quotes (\"like this, here\")",
                        "Line breaks separate rows, not nested data",
                    ],
                },
                { heading: "Why CSV matters in 2026",
                    body: "CSV has been around since the 1970s. It's older than the web. It's older than most spreadsheet software. And it's still the format every system on Earth understands — because it's simple, open, and tied to no one company.",
                    sub: [
                        { heading: "Universal compatibility", body: "Excel, Numbers, Google Sheets, MySQL, PostgreSQL, MongoDB, Shopify, Mailchimp, Salesforce, Stripe, every BI tool, every AI training pipeline — they all read and write CSV. There's no format negotiation. CSV just works." },
                        { heading: "Lightweight", body: "Plain text means no overhead. A 100,000-row CSV is often 5–10× smaller than the same data in .xlsx. It opens faster, emails easier, and streams over slow connections." },
                        { heading: "Future-proof", body: "Because CSV isn't tied to any one company, no software vendor can deprecate it. A CSV file written in 1985 still opens perfectly today. Try that with a Lotus 1-2-3 file." },
                    ],
                },
                { heading: "What CSV doesn't do", body: "CSV stores text data only. It has no formulas, no formatting, no multiple sheets, no charts, no images, no cell colours. If you need any of that, you want Excel (.xlsx), Numbers (.numbers), or Google Sheets. CSV is for raw data, not presentation." },
                { heading: "When to use CSV",
                    bullets: [
                        "Importing or exporting between two systems",
                        "Backing up structured data",
                        "Sharing a list with someone in a different software ecosystem",
                        "Feeding data into a script, AI pipeline, or automation",
                        "Storing data you might want to read in 20 years",
                    ],
                },
                { heading: "When NOT to use CSV",
                    bullets: [
                        "You need formulas or calculations (use Excel)",
                        "You're storing rich content (images, formatting)",
                        "You need real-time collaboration (use Sheets)",
                        "You need version history (use a database)",
                    ],
                },
            ]}
            faqs={faqsByTopic("what-is-csv", "general", "csv-vs-excel", "lightweight")}
            related={[RELATED.viewer, RELATED.editor, RELATED.vs, RELATED.use, RELATED.impexp, RELATED.faq]}
        />
    );
}
